import type {
    CommerceCatalogueExperience,
    CommerceProductSort,
    CommerceProductStatus,
    SearchCommerceProductsRequest,
} from "@teqtank/commerce-contracts";

const PRODUCT_SORT_VALUES = new Set<CommerceProductSort>([
    "default",
    "name-ascending",
    "name-descending",
    "price-ascending",
    "price-descending",
    "newest",
]);

const PRODUCT_STATUS_VALUES = new Set<CommerceProductStatus>([
    "active",
    "unavailable",
    "coming-soon",
    "discontinued",
]);

export type NormalizedProductSearchRequest = SearchCommerceProductsRequest &
    Required<Pick<SearchCommerceProductsRequest, "experience" | "page" | "pageSize" | "sort">>;

export function normalizeProductSearchRequest(
    request: SearchCommerceProductsRequest = {},
): NormalizedProductSearchRequest {
    const page = request.page ?? 1;
    const pageSize = request.pageSize ?? 24;
    const sort = request.sort ?? "default";
    const experience = normalizeCatalogueExperience(request.experience);

    if (!Number.isInteger(page) || page < 1) {
        throw new TypeError("Product search page must be an integer greater than or equal to one.");
    }

    if (!Number.isInteger(pageSize) || pageSize < 1 || pageSize > 100) {
        throw new TypeError(
            "Product search pageSize must be an integer between one and one hundred.",
        );
    }

    if (!PRODUCT_SORT_VALUES.has(sort)) {
        throw new TypeError(`Unsupported Product search sort '${String(sort)}'.`);
    }

    if (request.status !== undefined && !PRODUCT_STATUS_VALUES.has(request.status)) {
        throw new TypeError(`Unsupported Product status '${String(request.status)}'.`);
    }

    const query = normalizeOptionalString(request.query);
    const categoryId = normalizeOptionalString(request.categoryId);
    const catalogueId = normalizeOptionalString(request.catalogueId);
    const tags = normalizeTags(request.tags);

    return Object.freeze({
        page,
        pageSize,
        sort,
        experience,

        ...(query !== undefined
            ? {
                  query,
              }
            : {}),

        ...(categoryId !== undefined
            ? {
                  categoryId,
              }
            : {}),

        ...(catalogueId !== undefined
            ? {
                  catalogueId,
              }
            : {}),

        ...(tags !== undefined
            ? {
                  tags,
              }
            : {}),

        ...(request.status !== undefined
            ? {
                  status: request.status,
              }
            : {}),

        ...(request.recurringAvailable !== undefined
            ? {
                  recurringAvailable: request.recurringAvailable,
              }
            : {}),
    });
}

export function normalizeCatalogueExperience(
    experience: CommerceCatalogueExperience | undefined,
): CommerceCatalogueExperience {
    if (experience === undefined) {
        return "shop";
    }

    if (typeof experience !== "string" || experience.trim().length === 0) {
        throw new TypeError("Product catalogue experience must contain visible text.");
    }

    return experience.trim();
}

export function requireProductIdentifier(value: string, description: string): string {
    if (typeof value !== "string" || value.trim().length === 0) {
        throw new TypeError(`${description} must contain at least one non-whitespace character.`);
    }

    return value.trim();
}

function normalizeOptionalString(value: string | undefined): string | undefined {
    if (value === undefined) {
        return undefined;
    }

    const normalized = value.trim();

    return normalized.length === 0 ? undefined : normalized;
}

function normalizeTags(tags: readonly string[] | undefined): readonly string[] | undefined {
    if (tags === undefined) {
        return undefined;
    }

    const normalized = [
        ...new Set(tags.map((tag) => tag.trim().toLowerCase()).filter((tag) => tag.length > 0)),
    ].sort();

    return normalized.length === 0 ? undefined : Object.freeze(normalized);
}
